import { useEffect } from 'react';

type Opts = {
  enabled?: boolean;
  canUndo: boolean;
  canRedo: boolean;
  hasActive: boolean;
  currentTime: number;
  totalDuration: number;
  onPlayPause: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onSplit: () => void;
  onDelete: () => void;
  onSeek: (time: number) => void;
  onMute?: () => void;
};

function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return el.isContentEditable;
}

export function useEditorShortcuts({
  enabled = true,
  canUndo,
  canRedo,
  hasActive,
  currentTime,
  totalDuration,
  onPlayPause,
  onUndo,
  onRedo,
  onSplit,
  onDelete,
  onSeek,
  onMute,
}: Opts) {
  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (e: KeyboardEvent) => {
      const typing = isTypingTarget(e.target);
      const key = e.key.toLowerCase();
      const mod = e.ctrlKey || e.metaKey;

      // Timeline undo/redo even with chat focused when there is history to apply.
      if (mod && key === 'z' && e.shiftKey) {
        if (typing && !canRedo) return;
        e.preventDefault();
        if (canRedo) onRedo();
        return;
      }
      if (mod && key === 'z') {
        if (typing && !canUndo) return;
        e.preventDefault();
        if (canUndo) onUndo();
        return;
      }
      if (mod && key === 'y') {
        if (typing && !canRedo) return;
        e.preventDefault();
        if (canRedo) onRedo();
        return;
      }

      if (typing) return;

      if (key === ' ' || e.code === 'Space') {
        e.preventDefault();
        onPlayPause();
        return;
      }
      if (key === 's' && !mod) {
        e.preventDefault();
        if (hasActive) onSplit();
        return;
      }
      if ((key === 'delete' || key === 'backspace') && hasActive) {
        e.preventDefault();
        onDelete();
        return;
      }
      if (key === 'm' && hasActive && onMute) {
        e.preventDefault();
        onMute();
        return;
      }
      if (key === 'arrowleft') {
        e.preventDefault();
        onSeek(Math.max(0, currentTime - (e.shiftKey ? 0.1 : 1)));
        return;
      }
      if (key === 'arrowright') {
        e.preventDefault();
        onSeek(Math.min(totalDuration, currentTime + (e.shiftKey ? 0.1 : 1)));
        return;
      }
      if (key === 'home') {
        e.preventDefault();
        onSeek(0);
        return;
      }
      if (key === 'end') {
        e.preventDefault();
        onSeek(totalDuration);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [
    enabled, canUndo, canRedo, hasActive, currentTime, totalDuration,
    onPlayPause, onUndo, onRedo, onSplit, onDelete, onSeek, onMute,
  ]);
}
